import { useMemo, useReducer } from 'react';
import { defaultProgramFormValues, type ProgramFormData } from './ProgramFormConfig';

export type FormMode = 'create' | 'edit';

// State interface
export interface ProgramFormState {
    formData: ProgramFormData;
    mode: FormMode;
    isSubmitting: boolean;
}

// Actions interface
export interface ProgramFormActions {
    updateFormData: (data: Partial<ProgramFormData>) => void;
    submit: (data: Partial<ProgramFormData>) => void;
    submitStart: () => void;
    submitSuccess: () => void;
    submitError: () => void;
    reset: () => void;
    initialize: (data: Partial<ProgramFormData>) => void;
}

type ProgramFormAction =
    | { type: 'UPDATE_FORM_DATA'; payload: Partial<ProgramFormData> }
    | { type: 'SUBMIT'; payload: Partial<ProgramFormData> }
    | { type: 'SUBMIT_START' }
    | { type: 'SUBMIT_SUCCESS' }
    | { type: 'SUBMIT_ERROR' }
    | { type: 'RESET' }
    | { type: 'INITIALIZE'; payload: Partial<ProgramFormData> };

function programFormReducer(state: ProgramFormState, action: ProgramFormAction): ProgramFormState {
    switch (action.type) {
        case 'UPDATE_FORM_DATA':
            return {
                ...state,
                formData: { ...state.formData, ...action.payload },
            };
        case 'SUBMIT':
            return {
                ...state,
                isSubmitting: true,
            };
        case 'SUBMIT_START':
            return {
                ...state,
                isSubmitting: true,
            };
        case 'SUBMIT_SUCCESS':
        case 'SUBMIT_ERROR':
            return {
                ...state,
                isSubmitting: false,
            };
        case 'RESET':
            return {
                ...state,
                formData: defaultProgramFormValues,
                isSubmitting: false,
            };
        case 'INITIALIZE':
            return {
                ...state,
                formData: { ...defaultProgramFormValues, ...action.payload },
            };
        default:
            return state;
    }
}

export function useProgramFormState(mode: FormMode, initialData?: Partial<ProgramFormData>) {
    // Initialize state
    const initialState: ProgramFormState = {
        formData: initialData ? { ...defaultProgramFormValues, ...initialData } : defaultProgramFormValues,
        mode,
        isSubmitting: false,
    };

    // Create reducer
    const [state, dispatch] = useReducer(programFormReducer, initialState);
    const actions = useMemo<ProgramFormActions>(
        () => ({
            updateFormData: data => dispatch({ type: 'UPDATE_FORM_DATA', payload: data }),
            submit: data => dispatch({ type: 'SUBMIT', payload: data }),
            submitStart: () => dispatch({ type: 'SUBMIT_START' }),
            submitSuccess: () => dispatch({ type: 'SUBMIT_SUCCESS' }),
            submitError: () => dispatch({ type: 'SUBMIT_ERROR' }),
            reset: () => dispatch({ type: 'RESET' }),
            initialize: data => dispatch({ type: 'INITIALIZE', payload: data }),
        }),
        [dispatch]
    );

    return { state, actions };
}
